import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavShell } from "@/components/NavShell";
import { PenTool, Clock, TrendingUp, Zap, ArrowRight, CreditCard, FileText, Mail, Sparkles, ShoppingBag, Copy, Check, Gift, Users, BarChart3, Type } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const TEMPLATE_ICONS = {
  blog_post: FileText,
  social_media: Sparkles,
  marketing_copy: TrendingUp,
  email: Mail,
  product_description: ShoppingBag,
};

const TEMPLATE_LABELS = {
  blog_post: "Blog Posts",
  social_media: "Social Media",
  marketing_copy: "Marketing",
  email: "Emails",
  product_description: "Products",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg px-3 py-2 text-xs">
        <p className="text-gray-400">{label}</p>
        <p className="text-violet-300 font-semibold">{payload[0].value} generated</p>
      </div>
    );
  }
  return null;
};

export default function Dashboard({ user, apiClient, onLogout, refreshUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  useEffect(() => {
    loadStats();
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPayment(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      const res = await apiClient.get("/stats");
      setStats(res.data);
    } catch {}
  };

  const pollPayment = async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPollStatus("timeout"); return; }
    setPollStatus("checking");
    try {
      const res = await apiClient.get(`/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPollStatus("success");
        await refreshUser();
        await loadStats();
        window.history.replaceState({}, "", "/dashboard");
      } else if (res.data.status === "expired") {
        setPollStatus("expired");
      } else {
        setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    }
  };

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/auth?ref=${stats?.referral_code || user?.referral_code || ""}`;
    try { await navigator.clipboard.writeText(link); } catch {
      const ta = document.createElement('textarea'); ta.value = link; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 2000);
  };

  const chartData = stats?.daily_stats?.map(d => ({
    date: new Date(d.date).toLocaleDateString('en', { weekday: 'short' }),
    count: d.count,
  })) || [];

  return (
    <NavShell user={user} onLogout={onLogout} active="dashboard">
      <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8" data-testid="dashboard-page">
        {/* Payment status */}
        {pollStatus && (
          <div className={`rounded-xl p-4 border animate-fade-in-up ${pollStatus === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300" : pollStatus === "checking" ? "bg-violet-500/10 border-violet-500/20 text-violet-300" : "bg-red-500/10 border-red-500/20 text-red-300"}`} data-testid="payment-status-banner">
            {pollStatus === "checking" && <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-violet-300/30 border-t-violet-300 rounded-full animate-spin" /> Processing your payment...</span>}
            {pollStatus === "success" && "Payment successful! Credits have been added to your account."}
            {pollStatus === "timeout" && "Payment status check timed out. Credits will be added shortly."}
            {pollStatus === "expired" && "Payment session expired. Please try again."}
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            Welcome back, <span className="brand-gradient-text">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-gray-500">Here's your content generation overview.</p>
        </div>

        {/* Stat cards - Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 bg-gray-900/50 border border-white/5 rounded-xl p-6 relative overflow-hidden group hover:border-violet-500/20 transition-colors duration-300" data-testid="credits-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/5 rounded-full blur-[60px]" />
            <div className="relative">
              <div className="text-xs font-semibold tracking-wide uppercase text-gray-500 mb-3 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Available Credits</div>
              <div className="text-5xl font-bold brand-gradient-text mb-2" style={{ fontFamily: 'Outfit' }}>{user?.credits ?? 0}</div>
              <button onClick={() => navigate("/pricing")} className="inline-flex items-center gap-1.5 text-sm text-violet-400 hover:text-violet-300 transition-colors duration-200 mt-2" data-testid="buy-credits-link">
                <CreditCard className="w-3.5 h-3.5" /> Buy more credits <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="bg-gray-900/50 border border-white/5 rounded-xl p-6 hover:border-violet-500/20 transition-colors duration-300" data-testid="total-generated-card">
            <div className="text-xs font-semibold tracking-wide uppercase text-gray-500 mb-3 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> Total Generated</div>
            <div className="text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>{stats?.total_generated ?? 0}</div>
            <div className="text-sm text-gray-500">content pieces</div>
          </div>

          <div className="bg-gray-900/50 border border-white/5 rounded-xl p-6 hover:border-violet-500/20 transition-colors duration-300" data-testid="words-card">
            <div className="text-xs font-semibold tracking-wide uppercase text-gray-500 mb-3 flex items-center gap-1.5"><Type className="w-3.5 h-3.5" /> Words Written</div>
            <div className="text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>{(stats?.total_words ?? 0).toLocaleString()}</div>
            <div className="text-sm text-gray-500">total words</div>
          </div>
        </div>

        {/* Referral card */}
        <div className="bg-gradient-to-r from-violet-600/5 to-cyan-600/5 border border-violet-500/10 rounded-xl p-6" data-testid="referral-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                <Gift className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Give 5, Get 5</h3>
                <p className="text-sm text-gray-400">Share your referral link. You both get 5 free credits when they sign up.</p>
                {(stats?.referral_count > 0) && (
                  <div className="flex items-center gap-3 mt-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                      <Users className="w-3.5 h-3.5" /> {stats.referral_count} referral{stats.referral_count !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-violet-400">+{stats.referral_count * 5} credits earned</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-sm font-mono text-violet-300" data-testid="referral-code">
                {stats?.referral_code || user?.referral_code || "..."}
              </div>
              <button
                onClick={copyReferralLink}
                className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 text-white px-4 py-2.5 rounded-lg text-xs font-medium tracking-wide uppercase transition-colors duration-200 active:scale-95 shrink-0"
                data-testid="copy-referral-btn"
              >
                {refCopied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
              </button>
            </div>
          </div>
        </div>

        {/* Chart + Quick actions row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity chart */}
          <div className="lg:col-span-2 bg-gray-900/50 border border-white/5 rounded-xl p-6" data-testid="activity-chart">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-gray-500 mb-4">This Week's Activity</h2>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barCategoryGap="30%">
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={false} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={`rgba(139, 92, 246, ${0.4 + (i / chartData.length) * 0.6})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-sm text-gray-600">
                No activity this week. Generate some content to see your stats!
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div>
            <h2 className="text-sm font-semibold tracking-wide uppercase text-gray-500 mb-4">Quick Generate</h2>
            <div className="space-y-2">
              {Object.entries(TEMPLATE_LABELS).map(([key, label]) => {
                const Icon = TEMPLATE_ICONS[key] || FileText;
                const count = stats?.template_stats?.[key] || 0;
                return (
                  <button
                    key={key}
                    onClick={() => navigate(`/generate?template=${key}`)}
                    className="w-full template-card bg-gray-900/50 border border-white/5 rounded-xl p-3.5 text-left hover:border-violet-500/30 group flex items-center gap-3"
                    data-testid={`quick-${key}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0 group-hover:bg-violet-500/20 transition-colors duration-300">
                      <Icon className="w-4 h-4 text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{label}</div>
                    </div>
                    {count > 0 && <span className="text-xs text-gray-500">{count} created</span>}
                    <ArrowRight className="w-3.5 h-3.5 text-gray-600 group-hover:text-violet-400 transition-colors duration-200" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Template breakdown */}
        {stats?.template_stats && Object.keys(stats.template_stats).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>Usage Breakdown</h2>
            <div className="bg-gray-900/50 border border-white/5 rounded-xl p-6">
              <div className="space-y-3">
                {Object.entries(stats.template_stats).map(([key, count]) => {
                  const maxCount = Math.max(...Object.values(stats.template_stats));
                  const pct = (count / maxCount) * 100;
                  return (
                    <div key={key} className="flex items-center gap-4" data-testid={`breakdown-${key}`}>
                      <span className="text-sm text-gray-400 w-32 shrink-0">{TEMPLATE_LABELS[key] || key}</span>
                      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-600 to-indigo-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-medium text-gray-300 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recent content */}
        {stats?.recent_content && stats.recent_content.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Recent Content</h2>
              <button onClick={() => navigate("/history")} className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors duration-200" data-testid="view-all-history">
                View all <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-3">
              {stats.recent_content.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-gray-900/50 border border-white/5 rounded-xl p-4 hover:border-violet-500/20 transition-colors duration-300" data-testid={`recent-${item.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs font-medium tracking-wide uppercase text-violet-400">{TEMPLATE_LABELS[item.template] || item.template}</span>
                      {item.word_count && <span className="text-xs text-gray-600 ml-2">{item.word_count} words</span>}
                      <h3 className="text-sm font-medium mt-1 line-clamp-1">{item.topic}</h3>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0 ml-4">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-400 line-clamp-2">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {(!stats?.recent_content || stats.recent_content.length === 0) && (
          <div className="text-center py-16 bg-gray-900/30 border border-white/5 rounded-xl" data-testid="empty-state">
            <PenTool className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>No content yet</h3>
            <p className="text-gray-500 text-sm mb-6">Start generating your first piece of content</p>
            <button onClick={() => navigate("/generate")} className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 active:scale-95" data-testid="empty-generate-btn">
              <Zap className="w-4 h-4" /> Generate content
            </button>
          </div>
        )}
      </div>
    </NavShell>
  );
}
